import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { resolveMediaUrl } from "../lib/media";
import { postUrl } from "../lib/urls";

const TEASER_SLUGS = [
  "retire-to-travel",
  "lifes-little-blips-as-the-wanderlust-continues",
  "salinas-and-the-coast-of-ecuador",
];

export default function StoryTeasers() {
  const { getPost } = useContent();
  const essays = TEASER_SLUGS.map(getPost).filter(Boolean);
  if (essays.length === 0) return null;

  return (
    <section data-testid="story-teasers" className="bg-stone-50 py-24 lg:py-32 border-y border-stone-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          <div className="md:col-span-4 md:sticky md:top-28 self-start">
            <p className="kicker">Editor's Letter</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight mt-5 text-stone-900">
              Stories from the <em className="italic">slow road.</em>
            </h2>
            <p className="mt-5 text-stone-600 leading-relaxed text-[15px]">
              We stay in each place a month. We shop the farmers' markets. We cook in rented kitchens. We befriend the couple at the next table. These are the essays behind the photographs.
            </p>
          </div>

          <div className="md:col-span-8 space-y-1 border-t border-stone-300">
            {essays.map((post, i) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
              >
                <Link
                  to={`/post/${post.slug}`}
                  data-testid={`story-teaser-${post.slug}`}
                  className="group grid grid-cols-12 gap-6 py-8 border-b border-stone-300 items-center hover:bg-white transition-colors"
                >
                  <div className="col-span-3 sm:col-span-2 overflow-hidden aspect-square bg-stone-200">
                    <img src={resolveMediaUrl(post.hero)} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                  </div>
                  <div className="col-span-7 sm:col-span-8">
                    <p className="kicker text-stone-500 text-[10px]">{post.kicker} · {post.readTime} min</p>
                    <h3 className="font-serif text-2xl sm:text-3xl font-normal leading-snug mt-2 text-stone-900 group-hover:text-[#9c4122] transition-colors">
                      {post.title}
                    </h3>
                    <p className="hidden sm:block mt-2 text-stone-600 text-sm leading-relaxed line-clamp-2">{post.excerpt}</p>
                  </div>
                  <div className="col-span-2 sm:col-span-2 flex justify-end">
                    <span className="w-12 h-12 border border-stone-400 rounded-full flex items-center justify-center text-stone-800 group-hover:bg-stone-900 group-hover:text-white group-hover:border-stone-900 transition-all">
                      <ArrowUpRight size={18} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
